import React, { useState, useEffect } from "react";
import "../../styles/cart.css";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../loader/loader";
import { getImageUrl } from "../../utils/helper.jsx";
import { CustomerApi } from "../../api/customer.api.jsx";
import { OrderApi } from "../../api/order.api.jsx";
import { LocalStorageUtils } from "../../utils/localstorage.utils.jsx";
import { EVENT_NAME, PRODUCT } from "../../utils/constants.jsx";
import OTPVerification from "../Auth/mobileotp.jsx";
import DeleteAddressPopup from "../popup/deleteAddress.jsx";
import AddAddressPopup from "../popup/addAddress.jsx";
import AnalyticsService from "../../services/analytics.service";
import AddressList from "./addressList";
import { FeeApi } from "api/fee.api";

const CartPage = () => {
  const [cart, setCart] = useState([]);
  const [customerAddressList, setCustomerAddressList] = useState([]);
  const [showAddAddressPopup, setShowAddAddressPopup] = useState(false);
  const [selectedCustomerAddressId, setSelectedCustomerAddressId] =
    useState(null); // Track selected address
  const [isLoading, setIsLoading] = useState(false); // State for loader
  const [isButtonDisabled, setIsButtonDisabled] = useState(false); // State to disable the button
  const [selectedShop, setSelectedShop] = useState(null); // Track the selected shop
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [deleteAddressIndex, setDeleteAddressIndex] = useState(null); // To store the index of the address to delete
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [deliveryFeeDetails, setDeliveryFeeDetails] = useState(null);
  const [deliveryFeeLoading, setDeliveryFeeLoading] = useState(false);

  const [authToken, setAuthToken] = useState(
    LocalStorageUtils.get(LocalStorageUtils.LOCAL_STORAGE_KEY.AUTH_TOKEN)
  );

  const navigate = useNavigate(); // Initialize navigate for redirection

  const refreshAuthToken = () => {
    setAuthToken(
      LocalStorageUtils.get(LocalStorageUtils.LOCAL_STORAGE_KEY.AUTH_TOKEN)
    );
  };

  const getAddressList = async ({ canAddAddressPopup = false } = {}) => {
    if (authToken) {
      const addressListResponse = await CustomerApi.getAddressList();
      setCustomerAddressList(addressListResponse);

      if (addressListResponse.length >= 1) {
        setSelectedCustomerAddressId(addressListResponse[0].id);
      }

      if (!addressListResponse.length && canAddAddressPopup) {
        setShowAddAddressPopup(true);
      }
    }
  };

  const getDeliveryFee = async () => {
    const totalCartAmount = totalCartValue;
    if(!totalCartAmount) {
      return;
    }
    try {
      setDeliveryFeeLoading(true);
      const response = await FeeApi.getDeliveryFee({
        totalAmount: totalCartAmount,
      });
      setDeliveryFeeDetails(response);
      setDeliveryFeeLoading(false);
    } catch (error) {
      console.error("Error getting delivery fee:", error);
      setDeliveryFeeLoading(false);
    }
  };

  useEffect(() => {
    getDeliveryFee();
  }, [cart]);

  useEffect(() => {
    getAddressList();
  }, [authToken]);

  useEffect(() => {
    const cartData = LocalStorageUtils.getCart();

    // Convert cartData (object) to array
    const cartArray = cartData ? Object.values(cartData) : [];

    setCart(cartArray);

    getAddressList();

    // Set selected shop if any
    const shop = cartArray.length > 0 ? cartArray[0].shop : null;
    setSelectedShop(shop);

    AnalyticsService.trackEvent(EVENT_NAME.CART_VIEWED);
    AnalyticsService.recordSession(EVENT_NAME.CART_VIEWED);
  }, []);

  const handleIncreaseQuantity = (id) => {
    const product = cart.find((product) => product.id === id);
    const updatedCart = cart.map((product) =>
      product.id === id
        ? { ...product, quantity: product.quantity + 1 }
        : product
    );
    setCart(updatedCart);
    LocalStorageUtils.setCart(updatedCart);
    AnalyticsService.trackEvent(EVENT_NAME.CART_PRODUCT_QUANTITY_INCREASED, {
      productId: id,
      quantity: updatedCart.find((product) => product.id === id).quantity,
      name: product?.name,
      merchantId: product?.merchantId,
      merchantName: product?.merchant?.displayName,
    });
  };

  const handleDecreaseQuantity = (id) => {
    const product = cart.find((product) => product.id === id);
    const updatedCart = cart.map((product) =>
      product.id === id && product.quantity > (product.minQuantity || 1)
        ? { ...product, quantity: product.quantity - 1 }
        : product
    );
    setCart(updatedCart);
    LocalStorageUtils.setCart(updatedCart);
    AnalyticsService.trackEvent(EVENT_NAME.CART_PRODUCT_QUANTITY_DECREASED, {
      productId: id,
      quantity: updatedCart.find((product) => product.id === id).quantity,
      name: product?.name,
      merchantId: product?.merchantId,
      merchantName: product?.merchant?.displayName,
    });
  };

  const handleSelectAddress = (address) => {
    AnalyticsService.trackEvent(EVENT_NAME.ADDRESS_SELECTED, {
      addressId: address.id,
      addressName: address.name,
      addressMobile: address.mobile,
      addressCity: address.city,
      addressAreaId: address.areaId,
    });
    setSelectedCustomerAddressId(address.id);
  };

  const totalCartValue = cart.reduce((total, item) => {
    // Skip items with serviceType "Repair"
    if (item.type && item.type === PRODUCT.TYPE.SERVICE) {
      return total; // Do not add FinalPrice for "Repair" items
    }

    // Add FinalPrice * quantity for other items
    return total + (item.sellingPrice || 0) * (item.quantity || 0);
  }, 0);

  // Function to handle order placement
  const handlePlaceOrder = async () => {
    if (!authToken) {
      console.log("handlePlaceOrder", "authToken not found");
      setShowLoginPopup(true);
      return;
    }

    if (!selectedCustomerAddressId) {
      console.log("handlePlaceOrder", "selectedCustomerAddressId not found");
      toast.error("Please select an address!");
      if (!customerAddressList.length) {
        setShowAddAddressPopup(true);
      }
      return;
    }

    setIsLoading(true);
    setIsButtonDisabled(true);

    const orderData = {
      customerAddressId: selectedCustomerAddressId,
      productItems: cart.map((product) => ({
        productId: product.id,
        quantity: product.quantity,
      })),
    };

    console.log("-=-=orderData", orderData);

    try {
      console.log("handlePlaceOrder", "try");
      // Call the API to create the order
      const response = await OrderApi.createOrder(orderData);

      const address = customerAddressList.find(
        (address) => address.id === selectedCustomerAddressId
      );

      const analyticsData = {
        orderId: response.id,
        orderAmount: response.totalAmount,
        orderItemCount: cart.length,
        productNameList: cart.map((product) => product.name).join(", "),
        orderSummary: cart.map((product) => `>${product.name} from *${product.merchant?.displayName}* X *${product.quantity}*`).join("\n"),
        merchantNameList: cart.map((product) => product.merchant?.displayName).join(", "),
        customerName: address?.name,
        customerMobile: address?.mobile,
        address: address?.addressLine1,
        city: address?.city,
        areaId: address?.areaId,
      };

      AnalyticsService.trackEvent(EVENT_NAME.ORDER_PLACED, analyticsData);
      // Clear cart from localStorage and state
      LocalStorageUtils.removeCart();
      toast.success("Order placed successfully!"); // Show success toast
      setCart([]);

      navigate(`/order/${response.id}?isOrderPlacedNow=true`, { replace: true }); // Show the thank-you popup
    } catch (error) {
      console.error("Error placing order:", error);
      toast.error("Error placing order. Please try again."); // Show error toast
    } finally {
      setIsLoading(false); // Hide the loader
    }
  };

  const handleConfirmDelete = () => {
    const updatedAddresses = customerAddressList.filter(
      (_, i) => i !== deleteAddressIndex
    ); // Remove the address at the given index
    setCustomerAddressList(updatedAddresses); // Update state

    // Reset selected address if the deleted address was selected
    if (
      selectedCustomerAddressId === customerAddressList[deleteAddressIndex].id
    ) {
      setSelectedCustomerAddressId(null);
    }

    // TODO: Call delete address api & getAddressList();

    setShowConfirmPopup(false); // Close the confirmation popup
  };

  const handleDeleteAddress = async (addressId) => {
    const address = customerAddressList.find(
      (address) => address.id === addressId
    );
    const updatedAddresses = customerAddressList.filter(
      (address) => address.id !== addressId
    ); // Remove the address at the given index
    setCustomerAddressList(updatedAddresses); // Update state

    const analyticsData = {
      addressId,
      addressName: address?.name,
      addressMobile: address?.mobile,
      addressCity: address?.city,
      addressAreaId: address?.areaId,
    };

    AnalyticsService.trackEvent(EVENT_NAME.ADDRESS_DELETED, analyticsData);

    const deleteAddressResponse = await CustomerApi.deleteAddress(addressId);
    console.log("-=-=-=-deleteAddressResponse", deleteAddressResponse);

    // If the deleted address was selected, reset the selected address
    if (selectedCustomerAddressId === addressId) {
      setSelectedCustomerAddressId(null); // Reset selected address
    }
  };

  const handleAddAddressComplete = async (newAddressId) => {
    AnalyticsService.trackEvent(EVENT_NAME.ADDRESS_ADDED, {
      addressId: newAddressId,
    });
    await getAddressList();
    setSelectedCustomerAddressId(newAddressId);
  };

  const addOldAddress = async () => {
    const oldAddress = LocalStorageUtils.get(
      LocalStorageUtils.LOCAL_STORAGE_KEY.OLD_ADDRESS
    );
    if (oldAddress && oldAddress.length > 0) {
      for (const address of oldAddress) {
        try {
          const response = await CustomerApi.createAddress({
            name: address.userName,
            mobile: address.mobileNumber,
            addressLine1: address.address,
            city: address.village,
          });
          console.log("CustomerApi.createAddress from old address", response);
        } catch (error) {
          console.error("Error creating address from old address:", error);
        }
      }
      LocalStorageUtils.remove(LocalStorageUtils.LOCAL_STORAGE_KEY.OLD_ADDRESS);
    }
  };

  const loginSuccess = async () => {
    toast.success("Login successful!", {
      position: "top-center",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
    setShowLoginPopup(false);
    refreshAuthToken();

    await addOldAddress();

    // To remove captcha from msg91
    window.location.reload();
  };

  return (
    <div className="cart-page">
      <h1 className="cart-title">Your Cart</h1>
      {showConfirmPopup && (
        <DeleteAddressPopup
          onConfirmDelete={handleConfirmDelete}
          onClose={() => setShowConfirmPopup(false)}
        />
      )}

      <div className="cart-section">
        {cart.length === 0 ? (
          <p className="empty-cart-message">Your cart is empty!</p>
        ) : (
          cart.map((product) => (
            <motion.div
              key={product.id}
              className="product-card"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <img
                src={getImageUrl(product.imageUrl)}
                alt={product.name}
                className="product-image"
              />
              <div className="product-info">
                <h2 className="product-name">{product.name}</h2>
                {product.merchant && product.type === PRODUCT.TYPE.PRODUCT && (
                  <p className="product-shop">
                    <strong>Shop: </strong>
                    {product.merchant.displayName}
                  </p>
                )}
                <p className="product-price">
                  {product.type === PRODUCT.TYPE.SERVICE ? (
                    <>
                      <span>
                        {" "}
                        <b>Fees</b>: लोकेशन के हिसाब से{" "}
                      </span>
                    </>
                  ) : (
                    <>
                      {/* {product.name} from <b>{product.shop}</b> x {product.quantity} = ₹ */}
                      {product.name} X {product.quantity} = ₹
                      {product.sellingPrice * product.quantity}
                    </>
                  )}
                  {/* <span className="original-price">₹{product.price}</span> */}
                  {/* <span className="final-price">₹{product.FinalPrice}</span> */}
                </p>
                {product.minQuantity && product.minQuantity > 1 && (
                  <span style={{ color: "red" }}>
                    Min Order Quantity: {product.minQuantity}
                  </span>
                )}
              </div>
              <div className="cart-item-actions">
                <button
                  className="quantity-btn"
                  onClick={() => handleDecreaseQuantity(product.id)}
                >
                  -
                </button>
                <span className="quantity">{product.quantity}</span>
                <button
                  className="quantity-btn"
                  onClick={() => handleIncreaseQuantity(product.id)}
                >
                  +
                </button>
              </div>
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="cart-item-subtotal"
              >
                {product.type === PRODUCT.TYPE.SERVICE ? (
                  <>
                    <span> </span>
                  </>
                ) : (
                  <>Subtotal: ₹{product.sellingPrice * product.quantity}</>
                )}
              </motion.p>
            </motion.div>
          ))
        )}
      </div>

      {cart.length > 0 && (
        <>
          <AddressList
            customerAddressList={customerAddressList}
            selectedCustomerAddressId={selectedCustomerAddressId}
            handleSelectAddress={handleSelectAddress}
            handleDeleteAddress={handleDeleteAddress}
            handleAddAddress={() => {
              if (!authToken) {
                setShowLoginPopup(true);
                return;
              }
              setShowAddAddressPopup(true);
            }}
          />
          <div className="delivery-section">
            <h2 className="section-title">Delivery</h2>
            {/* <p className="delivery-charge">Delivery Charges: <b> Free</b></p> */}
            <p className="total-cart-value">Item Total: ₹{totalCartValue}</p>
            {
              deliveryFeeLoading ? (
                <p className="total-cart-value">Delivery Fee: <Loader /></p>
              ) : (
                deliveryFeeDetails?.deliveryFee ? (
                <>
                  <p className="total-cart-value">Delivery Fee: ₹{deliveryFeeDetails?.deliveryFee}</p>
                  <p className="total-amount">Total Amount: ₹{totalCartValue + deliveryFeeDetails?.deliveryFee}</p>
                </>
                ) : (
                  <>
                    <p className="total-cart-value">Delivery Fee: <b>Free 🎉</b></p>
                    <p className="total-amount">Total Amount: ₹{totalCartValue}</p>
                  </>
                )
              )
            }
          </div>
          {/* <div
						onClick={handelClick}
						className="googleAUthorize"
					>
						<span  >
							<img
								src="https://static-00.iconduck.com/assets.00/google-icon-512x512-tqc9el3r.png" />
							<p> {  "Sign In with Google"}</p>
						</span>
					</div> */}
          <button
            className="checkout-btn"
            onClick={handlePlaceOrder}
            disabled={isButtonDisabled} // Disable button if address is not selected
          >
            {isLoading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {" "}
                <Loader />
              </div>
            ) : (
              "Continue with COD"
            )}
          </button>
        </>
      )}

      {/* Add Address Popup */}
      {showAddAddressPopup && (
        <AddAddressPopup
          onClose={() => setShowAddAddressPopup(false)}
          onAddAddressComplete={handleAddAddressComplete}
        />
      )}

      {showLoginPopup && (
        <OTPVerification
          onSuccess={() => loginSuccess()}
          onClose={() => setShowLoginPopup(false)}
        />
      )}

      <ToastContainer
        id="cart-toast-container"
        style={{ width: "300px" }}
        position="top-center"
        autoClose={3000}
      />
    </div>
  );
};

export default CartPage;
